import { Input, Row, Select, Space, Table, type TableProps, Tabs, Tag } from 'antd';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import type { TabsProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDateAndTime } from '../../../utils/datetime.util';
import { getJomPayPaymentThunk } from '../../../services/jomPayPaymentService/jomPayPaymentThunk';
import { JomPayPaymentData } from '../../../features/jomPayPayment/jomPayPaymentSlice';
import AccountEnquiryModal from '../../AccountEnquiry/AccountEnquiryModal';
import { numberWithCommas } from '../../../helpers/number';

const { Search } = Input;

const JomPayPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');
  const { jomPayPayment } = useSelector((state: RootState) => state.jomPayPayment);
  const [searchFiled, setSearchFiled] = useState('agreementNo');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchState, setSearchState] = useState(initialParams.search);

  const tablePagination = {
    total: jomPayPayment?.data?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: jomPayPayment?.data?.currentPage,
    pageSize: jomPayPayment?.data?.pageSize,
  };

  const fetchJomPayPayment = async (params?: any) => {
    await dispatch(getJomPayPaymentThunk({ params }));
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    fetchJomPayPayment(params);
  });

  const handleTableChange: TableProps<JomPayPaymentData>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      sorter?.order == null
        ? {
            ...initialParams,
            ...filterState,
            pagination,
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          };

    fetchJomPayPayment(param);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  const onSearch = (value: string) => {
    value = value.trim();
    fetchJomPayPayment({
      ...initialParams,
      filters: filterState,
      search: { [searchFiled]: value },
    });
    setSearchState({ [searchFiled]: value });
  };

  const columns: ColumnsType<JomPayPaymentData> = [
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      render: (text, record) => <AccountEnquiryModal agreementNo={record?.agreementNo ?? ''} />,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text: number) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Pay Date',
      dataIndex: 'payDate',
      render: (text, record) => displayDateAndTime(record.payDate),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
    },
    {
      title: 'Reference No',
      dataIndex: 'referenceNo',
      key: 'referenceNo',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text, record) => displayDateAndTime(record.createdAt),
    },
  ];

  return (
    <>
      <PageHeader className="px-0 mb-2" title="JomPay" />
      <Row justify="start" className="mb-2" align="bottom">
        <Space>
          <Input.Group compact>
            <Select
              defaultValue="agreementNo"
              style={{ width: 200 }}
              onChange={(value) => setSearchFiled(value)}
              options={[
                { value: 'agreementNo', label: 'Agreement No' },
                { value: 'channel', label: 'Channel' },
              ]}
            />
            <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
          </Input.Group>{' '}
        </Space>
      </Row>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        dataSource={jomPayPayment?.data?.data ?? []}
        columns={columns}
        pagination={tablePagination}
        onChange={handleTableChange}
      />
    </>
  );
};

export default JomPayPage;
